// frontend/src/pages/Exchange.jsx
// DwiPay Exchange — Buy USDT with UGX at live rates. USDT credited instantly.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { exchangeAPI, priceAPI, fxAPI, walletAPI } from '../services/api';
import { Card, Button, Badge, Spinner, Empty, Table, Td, SectionHeader } from '../components/ui';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { ArrowLeftRight, RefreshCw, Clock, CheckCircle, XCircle, Copy, AlertCircle, TrendingUp, Zap, SendHorizonal } from 'lucide-react';
import { format } from 'date-fns';

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtUGX(n) { return 'UGX ' + (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }); }
function fmtN(n, d = 4) { return (+(n || 0)).toFixed(d); }
function shortAddr(addr) {
  if (!addr || addr.length <= 16) return addr;
  return addr.slice(0, 6) + '…' + addr.slice(-4);
}

const STATUS_COLOR = { pending: 'amber', approved: 'cyan', sent: 'green', completed: 'green', rejected: 'red', cancelled: 'gray' };
const STATUS_ICON  = { pending: '⏳', approved: '✅', sent: '🚀', completed: '✔️', rejected: '❌', cancelled: '🚫' };

// ── Main component ────────────────────────────────────────────────────────────

export default function Exchange() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('buy');

  // Price engine state
  const [priceData, setPriceData]     = useState(null);
  const [priceAge, setPriceAge]       = useState(0);
  const [priceLoading, setPriceLoading] = useState(false);

  // Buy form state
  const [ugxBalance, setUgxBalance]   = useState(0);
  const [usdtBalance, setUsdtBalance] = useState(0);
  const [ugxAmount, setUgxAmount]     = useState('');
  const [quote, setQuote]             = useState(null);
  const [quoting, setQuoting]         = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [successOrder, setSuccessOrder] = useState(null);  // shown after buy

  // Sell USDT state
  const [sellAmount, setSellAmount]     = useState('');
  const [sellQuote, setSellQuote]       = useState(null);
  const [sellQuoting, setSellQuoting]   = useState(false);
  const [sellSubmitting, setSellSubmitting] = useState(false);
  const [successSell, setSuccessSell]   = useState(null);
  const sellQuoteTimer = useRef(null);

  // History
  const [history, setHistory]         = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // FX secondary tab
  const [fxRates, setFxRates]         = useState([]);
  const [fxFrom, setFxFrom]           = useState('USD');
  const [fxTo, setFxTo]               = useState('UGX');
  const [fxAmount, setFxAmount]       = useState('');
  const [fxQuote, setFxQuote]         = useState(null);
  const [fxQuoting, setFxQuoting]     = useState(false);
  const [fxConverting, setFxConverting] = useState(false);

  const quoteTimer   = useRef(null);
  const priceTimer   = useRef(null);
  const priceAgeRef  = useRef(null);

  // ── Live price feed ─────────────────────────────────────────────────────────

  const fetchPrice = useCallback(async (silent = false) => {
    if (!silent) setPriceLoading(true);
    try {
      const [priceRes, walletRes] = await Promise.allSettled([
        priceAPI.getUsdt(),
        walletAPI.getBalance(),
      ]);
      if (priceRes.status  === 'fulfilled') {
        setPriceData(priceRes.value.price);
        setPriceAge(0);
      }
      if (walletRes.status === 'fulfilled') {
        setUgxBalance(walletRes.value.wallet?.ugxBalance    || 0);
        setUsdtBalance(walletRes.value.wallet?.usdtBalance  || 0);
      }
    } catch { /* silent */ }
    finally { if (!silent) setPriceLoading(false); }
  }, []);

  useEffect(() => {
    fetchPrice();
    // Auto-refresh every 30 seconds
    priceTimer.current = setInterval(() => fetchPrice(true), 30000);
    // Increment age counter every second
    priceAgeRef.current = setInterval(() => setPriceAge(a => a + 1), 1000);
    return () => {
      clearInterval(priceTimer.current);
      clearInterval(priceAgeRef.current);
    };
  }, [fetchPrice]);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const res = await exchangeAPI.getHistory({ limit: 30 });
      setHistory(res.orders || []);
    } catch { setHistory([]); }
    finally { setHistLoading(false); }
  }, []);

  const loadFxRates = useCallback(async () => {
    try { const res = await fxAPI.getRates().catch(() => ({ rates: [] })); setFxRates(res.rates || []); }
    catch { /* silent */ }
  }, []);

  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);
  useEffect(() => { if (tab === 'fx')      loadFxRates(); }, [tab, loadFxRates]);

  // ── Auto-quote on UGX input change ─────────────────────────────────────────

  useEffect(() => {
    const amt = parseFloat(ugxAmount);
    if (!amt || amt <= 0 || !priceData) { setQuote(null); return; }

    clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(async () => {
      setQuoting(true);
      try {
        const res = await exchangeAPI.getQuote(amt);
        setQuote(res.quote);
      } catch (err) {
        setQuote(null);
        if (err.message) toast.error(err.message, { id: 'q-err' });
      } finally { setQuoting(false); }
    }, 400);
    return () => clearTimeout(quoteTimer.current);
  }, [ugxAmount, priceData]);

  // ── Auto sell quote ─────────────────────────────────────────────────────────

  useEffect(() => {
    const amt = parseFloat(sellAmount);
    if (!amt || amt <= 0 || !priceData) { setSellQuote(null); return; }
    clearTimeout(sellQuoteTimer.current);
    sellQuoteTimer.current = setTimeout(async () => {
      setSellQuoting(true);
      try {
        const res = await exchangeAPI.getSellQuote(amt);
        setSellQuote(res.quote);
      } catch (err) {
        setSellQuote(null);
        if (err.message) toast.error(err.message, { id: 'sq-err' });
      } finally { setSellQuoting(false); }
    }, 400);
    return () => clearTimeout(sellQuoteTimer.current);
  }, [sellAmount, priceData]);

  // ── Submit buy ──────────────────────────────────────────────────────────────

  const handleBuy = async () => {
    if (!quote) return toast.error('Please wait for the quote to load');
    setSubmitting(true);
    try {
      const res = await exchangeAPI.createOrder({ ugxAmount: parseFloat(ugxAmount) });
      setSuccessOrder(res.order);
      setUgxAmount('');
      setQuote(null);
      fetchPrice(true);
    } catch (err) {
      toast.error(err.message || 'Failed to submit order');
    } finally { setSubmitting(false); }
  };

  // ── Submit sell ─────────────────────────────────────────────────────────────

  const handleSell = async () => {
    if (!sellQuote) return toast.error('Please wait for the quote to load');
    setSellSubmitting(true);
    try {
      const res = await exchangeAPI.createSellOrder({ usdtAmount: parseFloat(sellAmount) });
      setSuccessSell(res.order);
      setSellAmount('');
      setSellQuote(null);
      fetchPrice(true);
    } catch (err) {
      toast.error(err.message || 'Failed to submit sell order');
    } finally { setSellSubmitting(false); }
  };

  // ── FX secondary tab ────────────────────────────────────────────────────────

  const handleFxQuote = useCallback(async () => {
    if (!fxAmount || parseFloat(fxAmount) <= 0 || fxFrom === fxTo) return;
    setFxQuoting(true);
    try { const res = await fxAPI.quote(fxFrom, fxTo, parseFloat(fxAmount)); setFxQuote(res.quote || res); }
    catch (err) { toast.error(err.message || 'Could not get quote'); setFxQuote(null); }
    finally { setFxQuoting(false); }
  }, [fxAmount, fxFrom, fxTo]);

  useEffect(() => {
    if (!fxAmount || parseFloat(fxAmount) <= 0) { setFxQuote(null); return; }
    const t = setTimeout(handleFxQuote, 600);
    return () => clearTimeout(t);
  }, [fxAmount, fxFrom, fxTo, handleFxQuote]);

  const handleFxConvert = async () => {
    if (!fxQuote) return toast.error('Please get a quote first');
    setFxConverting(true);
    try {
      const res = await fxAPI.convert({ fromCurrency: fxFrom, toCurrency: fxTo, amount: parseFloat(fxAmount) });
      toast.success(res.message || `Converted ${fxAmount} ${fxFrom} → ${fxTo}`);
      setFxAmount(''); setFxQuote(null); fetchPrice(true);
    } catch (err) { toast.error(err.message || 'Conversion failed'); }
    finally { setFxConverting(false); }
  };

  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success('Copied!', { duration: 1500 }));

  // ── Compute live stats ───────────────────────────────────────────────────────

  const buyRate      = priceData?.buyUgxPerUsdt   || 0;
  const sellRate     = priceData?.sellUgxPerUsdt  || 0;
  const marketRate   = priceData?.marketUgxPerUsdt || 0;
  const spreadPct    = priceData?.buySpread ? (priceData.buySpread * 100).toFixed(1) : null;
  const priceIsFresh = priceAge < 60;

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-text-primary tracking-tight">DwiPay Exchange</h1>
          <p className="text-text-secondary text-sm mt-1">Buy USDT with UGX at live rates — credited instantly to your DwiPay wallet.</p>
        </div>
        <button onClick={() => fetchPrice()} className="text-text-muted hover:text-cyan p-2 rounded-xl hover:bg-cyan/10 transition-all" title="Refresh rates">
          <RefreshCw className={clsx('w-4 h-4', priceLoading && 'animate-spin')} />
        </button>
      </div>

      {/* Live price ticker */}
      <div className="bg-gradient-to-r from-cyan/10 via-bg-panel to-bg-panel border border-cyan/20 rounded-2xl p-4 flex flex-wrap gap-6 items-center">
        {priceLoading && !priceData ? (
          <div className="flex items-center gap-2 text-text-muted"><Spinner size="sm" /><span className="text-sm">Loading live rate…</span></div>
        ) : (
          <>
            <div>
              <p className="text-[10px] text-text-muted uppercase tracking-widest font-bold mb-0.5">DwiPay Buy Price</p>
              <p className="text-3xl font-black text-cyan">{fmtUGX(buyRate)}</p>
              <p className="text-text-muted text-[10px] mt-0.5">per 1 USDT</p>
            </div>
            {marketRate > 0 && (
              <div>
                <p className="text-[10px] text-text-muted uppercase tracking-widest font-bold mb-0.5">Market Price</p>
                <p className="text-lg font-bold text-text-secondary">{fmtUGX(marketRate)}</p>
                {spreadPct && <p className="text-[10px] text-amber">{spreadPct}% spread</p>}
              </div>
            )}
            <div>
              <p className="text-[10px] text-text-muted uppercase tracking-widest font-bold mb-0.5">DwiPay Sell Price</p>
              <p className="text-xl font-black text-green">{fmtUGX(sellRate)}</p>
              <p className="text-text-muted text-[10px] mt-0.5">per 1 USDT sold</p>
            </div>
            <div>
              <p className="text-[10px] text-text-muted uppercase tracking-widest font-bold mb-0.5">Your UGX</p>
              <p className="text-lg font-bold text-text-primary">{fmtUGX(ugxBalance)}</p>
            </div>
            <div>
              <p className="text-[10px] text-text-muted uppercase tracking-widest font-bold mb-0.5">Internal USDT</p>
              <p className="text-lg font-bold text-green">₮ {fmtN(usdtBalance, 4)}</p>
            </div>
            <div className="ml-auto flex flex-col items-end gap-1">
              <Badge label={priceData?.enabled !== false ? 'Exchange Open' : 'Paused'} color={priceData?.enabled !== false ? 'green' : 'red'} />
              <p className="text-[10px] text-text-muted flex items-center gap-1">
                <span className={clsx('w-1.5 h-1.5 rounded-full', priceIsFresh ? 'bg-green' : 'bg-amber')} />
                {priceData?.priceSource === 'coingecko' ? 'CoinGecko' : 'Manual'} · {priceAge}s ago
              </p>
              {priceData?.instantCredit && (
                <span className="text-[10px] text-cyan flex items-center gap-1">
                  <Zap className="w-2.5 h-2.5" /> Instant credit
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Success panel (shown after a successful buy) */}
      {successOrder && (
        <div className="bg-green/10 border border-green/30 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green flex-shrink-0" />
            <div>
              <p className="text-green font-black text-lg">₮ {successOrder.netAmount} USDT Added!</p>
              <p className="text-text-secondary text-sm">Your USDT has been credited to your DwiPay wallet instantly.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setSuccessOrder(null); setTab('history'); loadHistory(); }}>
              <Clock className="w-4 h-4" /> View Order History
            </Button>
            <Button size="sm" onClick={() => navigate('/crypto-withdraw')}>
              <SendHorizonal className="w-4 h-4" /> Withdraw to External Wallet
            </Button>
            <button onClick={() => setSuccessOrder(null)} className="text-text-muted text-xs hover:text-text-primary ml-auto">Dismiss</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[['buy', '₮ Buy USDT'], ['sell', '₮ Sell USDT'], ['history', 'My Orders'], ['fx', 'FX Convert']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={clsx('px-4 py-2 rounded-xl text-sm font-semibold transition-all',
              tab === id ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi'
            )}>{label}</button>
        ))}
      </div>

      {/* ── BUY USDT ── */}
      {tab === 'buy' && (
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <Card>
              <SectionHeader title="Buy USDT with UGX" />
              <div className="space-y-5">

                {/* UGX amount */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-text-muted uppercase tracking-wider">UGX Amount</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted text-xs font-bold">UGX</span>
                    <input
                      type="number"
                      className="w-full bg-bg-panel border border-border rounded-xl pl-12 pr-20 py-4 text-text-primary text-xl font-bold outline-none focus:border-cyan focus:ring-1 focus:ring-cyan/30 placeholder:text-text-muted"
                      placeholder="0"
                      value={ugxAmount}
                      onChange={e => setUgxAmount(e.target.value)}
                      min="0"
                    />
                    <button
                      onClick={() => setUgxAmount(Math.floor(ugxBalance).toString())}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-cyan bg-cyan/10 px-2 py-1 rounded-lg hover:bg-cyan/20 transition-all"
                    >MAX</button>
                  </div>
                  <p className="text-xs text-text-muted">Available: <span className="text-cyan font-semibold">{fmtUGX(ugxBalance)}</span></p>
                </div>

                {/* Live quote result */}
                <div className={clsx(
                  'rounded-xl border p-4 transition-all',
                  quoting ? 'bg-bg-panel border-border' :
                  quote   ? 'bg-cyan/5 border-cyan/30' : 'bg-bg-panel border-border'
                )}>
                  {quoting ? (
                    <div className="flex items-center gap-2 text-text-muted text-sm"><Spinner size="sm" /> Calculating…</div>
                  ) : quote ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Gross USDT</span>
                        <span className="font-semibold">₮ {fmtN(quote.grossAmount)}</span>
                      </div>
                      {(quote.networkFee > 0 || quote.platformFee > 0) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-text-muted">Fees (network + platform)</span>
                          <span className="text-amber font-semibold">− ₮ {fmtN(quote.totalFees || (quote.networkFee + quote.platformFee))}</span>
                        </div>
                      )}
                      <div className="border-t border-border/60 my-1" />
                      <div className="flex justify-between items-center">
                        <span className="text-text-primary font-bold">You receive</span>
                        <span className="text-green font-black text-2xl">₮ {fmtN(quote.netAmount)}</span>
                      </div>
                      <p className="text-[10px] text-text-muted">
                        Rate: {fmtUGX(quote.buyUgxPerUsdt || quote.ugxPerUsdt)} / USDT
                        {quote.instantCredit && <span className="text-cyan ml-2">⚡ Instant credit</span>}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-text-muted text-sm">
                      <AlertCircle className="w-4 h-4" />
                      Enter a UGX amount to see how much USDT you will receive
                    </div>
                  )}
                </div>

                {/* Instant credit notice */}
                {priceData?.instantCredit && (
                  <div className="flex items-start gap-3 bg-cyan/5 border border-cyan/20 rounded-xl p-3">
                    <Zap className="w-4 h-4 text-cyan flex-shrink-0 mt-0.5" />
                    <p className="text-cyan text-xs leading-relaxed">
                      USDT will be credited <strong>instantly</strong> to your DwiPay Internal Wallet.
                      You can then withdraw it to Binance, Trust Wallet, OKX, Bybit, or MetaMask.
                    </p>
                  </div>
                )}

                <Button
                  className="w-full"
                  loading={submitting}
                  disabled={!quote || quoting}
                  onClick={handleBuy}
                >
                  <TrendingUp className="w-4 h-4" />
                  Buy {quote ? `₮ ${fmtN(quote.netAmount)}` : 'USDT'}
                </Button>

                {/* Withdraw CTA */}
                <button
                  onClick={() => navigate('/crypto-withdraw')}
                  className="w-full text-xs text-text-muted hover:text-cyan text-center transition-colors py-1 flex items-center justify-center gap-1"
                >
                  <SendHorizonal className="w-3 h-3" /> Already have USDT? Withdraw to external wallet →
                </button>
              </div>
            </Card>
          </div>

          {/* Right panel */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <SectionHeader title="How It Works" />
              <div className="space-y-4">
                {[
                  { n: 1, t: 'Deposit UGX',       d: 'Fund your DwiPay Wallet via MTN MoMo or Airtel Money.' },
                  { n: 2, t: 'Buy USDT',           d: 'Enter the amount. We show you the exact USDT you receive at our live rate.' },
                  { n: 3, t: 'Instant credit',      d: 'USDT appears in your DwiPay Internal Wallet in seconds. No waiting.' },
                  { n: 4, t: 'Keep or withdraw',   d: 'Use your USDT for future trades, or withdraw to Binance, OKX, MetaMask, or any compatible wallet.' },
                ].map(({ n, t, d }) => (
                  <div key={n} className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-cyan flex items-center justify-center text-bg font-black text-xs flex-shrink-0 mt-0.5">{n}</div>
                    <div>
                      <p className="text-text-primary text-xs font-bold">{t}</p>
                      <p className="text-text-muted text-xs mt-0.5 leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <SectionHeader title="Rate Breakdown" />
              {priceData ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-muted">Market price</span>
                    <span className="font-semibold">{fmtUGX(priceData.marketUgxPerUsdt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Buy spread ({(priceData.buySpread * 100).toFixed(1)}%)</span>
                    <span className="text-amber font-semibold">+ {fmtUGX(priceData.marginPerUsdt)}</span>
                  </div>
                  <div className="border-t border-border/60 my-1" />
                  <div className="flex justify-between">
                    <span className="text-text-primary font-bold">DwiPay rate</span>
                    <span className="text-cyan font-bold">{fmtUGX(priceData.buyUgxPerUsdt)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-text-muted">Network fee</span>
                    <span>₮ {priceData.networkFee}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-text-muted">Platform fee</span>
                    <span>₮ {priceData.platformFee}</span>
                  </div>
                </div>
              ) : (
                <p className="text-text-muted text-xs">Loading…</p>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* ── SELL USDT ── */}
      {tab === 'sell' && (
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <Card>
              <SectionHeader title="Sell USDT for UGX" />
              <div className="space-y-5">

                {successSell && (
                  <div className="bg-green/10 border border-green/30 rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green flex-shrink-0" />
                      <div>
                        <p className="text-green font-black">UGX {successSell.netAmount?.toLocaleString()} Credited!</p>
                        <p className="text-text-secondary text-xs">USDT has been converted and credited to your UGX wallet.</p>
                      </div>
                    </div>
                    <button onClick={() => setSuccessSell(null)} className="text-text-muted text-xs hover:text-text-primary">Dismiss</button>
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-text-muted uppercase tracking-wider">USDT Amount to Sell</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted text-xs font-bold">₮</span>
                    <input
                      type="number"
                      className="w-full bg-bg-panel border border-border rounded-xl pl-8 pr-20 py-4 text-text-primary text-xl font-bold outline-none focus:border-cyan focus:ring-1 focus:ring-cyan/30 placeholder:text-text-muted"
                      placeholder="0.0000"
                      value={sellAmount}
                      onChange={e => setSellAmount(e.target.value)}
                      min="0"
                      step="0.0001"
                    />
                    <button
                      onClick={() => setSellAmount(usdtBalance.toFixed(4))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-cyan bg-cyan/10 px-2 py-1 rounded-lg hover:bg-cyan/20 transition-all"
                    >MAX</button>
                  </div>
                  <p className="text-xs text-text-muted">Available: <span className="text-green font-semibold">₮ {fmtN(usdtBalance, 4)}</span></p>
                </div>

                <div className={clsx('rounded-xl border p-4 transition-all', sellQuoting ? 'bg-bg-panel border-border' : sellQuote ? 'bg-green/5 border-green/30' : 'bg-bg-panel border-border')}>
                  {sellQuoting ? (
                    <div className="flex items-center gap-2 text-text-muted text-sm"><Spinner size="sm" /> Calculating…</div>
                  ) : sellQuote ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">USDT sold</span>
                        <span className="font-semibold">₮ {fmtN(parseFloat(sellAmount), 4)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Sell rate</span>
                        <span className="font-semibold">{fmtUGX(sellQuote.sellUgxPerUsdt)} / USDT</span>
                      </div>
                      <div className="border-t border-border/60 my-1" />
                      <div className="flex justify-between items-center">
                        <span className="text-text-primary font-bold">You receive</span>
                        <span className="text-green font-black text-2xl">{fmtUGX(sellQuote.ugxReceived)}</span>
                      </div>
                      <p className="text-[10px] text-cyan flex items-center gap-1"><Zap className="w-2.5 h-2.5" /> Instant credit to your UGX wallet</p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-text-muted text-sm">
                      <AlertCircle className="w-4 h-4" />
                      Enter a USDT amount to see how much UGX you will receive
                    </div>
                  )}
                </div>

                <Button className="w-full" loading={sellSubmitting} disabled={!sellQuote || sellQuoting} onClick={handleSell}>
                  <TrendingUp className="w-4 h-4" />
                  Sell {sellQuote ? `₮ ${fmtN(parseFloat(sellAmount), 4)} → ${fmtUGX(sellQuote.ugxReceived)}` : 'USDT'}
                </Button>
              </div>
            </Card>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <Card>
              <SectionHeader title="Sell Rate" />
              {priceData ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-muted">Market price</span>
                    <span className="font-semibold">{fmtUGX(marketRate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Sell spread ({priceData.sellSpread ? (priceData.sellSpread * 100).toFixed(1) : '2.0'}%)</span>
                    <span className="text-amber font-semibold">− {fmtUGX(marketRate - sellRate)}</span>
                  </div>
                  <div className="border-t border-border/60 my-1" />
                  <div className="flex justify-between">
                    <span className="text-text-primary font-bold">DwiPay sell rate</span>
                    <span className="text-green font-bold">{fmtUGX(sellRate)}</span>
                  </div>
                  <p className="text-text-muted text-[10px] mt-2">The spread is DWI FINTECH's trading margin. No additional fees.</p>
                </div>
              ) : <p className="text-text-muted text-xs">Loading…</p>}
            </Card>

            <Card>
              <SectionHeader title="How Selling Works" />
              <div className="space-y-3">
                {[
                  { n: 1, t: 'Enter USDT amount', d: 'Type how much USDT you want to sell.' },
                  { n: 2, t: 'See live quote',     d: 'We show the exact UGX you receive at our sell rate.' },
                  { n: 3, t: 'Instant UGX credit', d: 'UGX is credited instantly to your DwiPay wallet. No waiting.' },
                  { n: 4, t: 'Withdraw UGX',       d: 'Send UGX to MTN MoMo, Airtel Money, or any mobile wallet.' },
                ].map(({ n, t, d }) => (
                  <div key={n} className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-green flex items-center justify-center text-bg font-black text-xs flex-shrink-0 mt-0.5">{n}</div>
                    <div>
                      <p className="text-text-primary text-xs font-bold">{t}</p>
                      <p className="text-text-muted text-xs mt-0.5 leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ── MY ORDERS ── */}
      {tab === 'history' && (
        <Card>
          <SectionHeader
            title="Order History"
            action={
              <button onClick={loadHistory} className="text-cyan text-xs hover:underline flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            }
          />
          {histLoading ? (
            <div className="flex justify-center py-12"><Spinner size="lg" /></div>
          ) : history.length === 0 ? (
            <Empty icon={<Clock className="w-12 h-12 text-text-muted" />} title="No orders yet" description="Your exchange orders will appear here." />
          ) : (
            <Table headers={['Date', 'UGX Paid', 'USDT Received', 'Rate', 'Type', 'Status', 'TX']}>
              {history.map(o => (
                <tr key={o._id}>
                  <Td className="text-xs text-text-muted whitespace-nowrap">
                    {format(new Date(o.createdAt), 'dd MMM yy HH:mm')}
                  </Td>
                  <Td className="font-semibold text-sm">{fmtUGX(o.fromAmount)}</Td>
                  <Td className="font-black text-green text-sm">
                    ₮ {fmtN(o.netAmount)}
                    <span className="text-text-muted text-[10px] block">gross: {fmtN(o.grossAmount)}</span>
                  </Td>
                  <Td className="text-xs text-text-secondary">
                    {fmtUGX(o.buyUgxPerUsdt || o.ugxPerUsdt)}
                    {o.marketUgxPerUsdt && o.marketUgxPerUsdt !== (o.buyUgxPerUsdt || o.ugxPerUsdt) && (
                      <span className="text-text-muted block text-[10px]">market: {fmtUGX(o.marketUgxPerUsdt)}</span>
                    )}
                  </Td>
                  <Td>
                    {o.internalCredit
                      ? <Badge label="⚡ Instant" color="cyan" />
                      : <Badge label="Manual" color="gray" />}
                  </Td>
                  <Td>
                    <Badge label={STATUS_ICON[o.status] + ' ' + o.status} color={STATUS_COLOR[o.status] || 'gray'} />
                  </Td>
                  <Td>
                    {o.txHash ? (
                      <button onClick={() => copy(o.txHash)} className="text-xs font-mono text-cyan hover:underline flex items-center gap-1">
                        {o.txHash.slice(0, 8)}… <Copy className="w-3 h-3" />
                      </button>
                    ) : o.internalCredit ? (
                      <span className="text-text-muted text-xs">Internal</span>
                    ) : (
                      <span className="text-text-muted text-xs">Pending</span>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* ── FX CONVERT ── */}
      {tab === 'fx' && (
        <Card>
          <SectionHeader title="Currency Converter" />
          <div className="max-w-sm space-y-4">
            <div className="flex gap-2">
              <div className="flex-1 space-y-1">
                <label className="text-xs font-bold text-text-muted uppercase tracking-wider">From</label>
                <select className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2 text-text-primary text-sm outline-none focus:border-cyan"
                  value={fxFrom} onChange={e => setFxFrom(e.target.value)}>
                  {['USD', 'UGX', 'EUR', 'GBP'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex items-end pb-1">
                <button onClick={() => { const t = fxFrom; setFxFrom(fxTo); setFxTo(t); }}
                  className="p-2 rounded-xl bg-bg-panel border border-border hover:border-cyan text-text-muted hover:text-cyan transition-all">
                  <ArrowLeftRight className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 space-y-1">
                <label className="text-xs font-bold text-text-muted uppercase tracking-wider">To</label>
                <select className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2 text-text-primary text-sm outline-none focus:border-cyan"
                  value={fxTo} onChange={e => setFxTo(e.target.value)}>
                  {['UGX', 'USD', 'EUR', 'GBP'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-text-muted uppercase tracking-wider">Amount</label>
              <input type="number" className="w-full bg-bg-panel border border-border rounded-xl px-4 py-3 text-text-primary text-lg font-bold outline-none focus:border-cyan placeholder:text-text-muted"
                placeholder="0.00" value={fxAmount} onChange={e => setFxAmount(e.target.value)} />
            </div>

            {fxQuoting && (
              <div className="flex items-center gap-2 text-text-muted text-sm"><Spinner size="sm" /> Getting quote…</div>
            )}
            {fxQuote && !fxQuoting && (
              <div className="bg-cyan/5 border border-cyan/20 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-muted">You send</span>
                  <span className="font-semibold">{fxAmount} {fxFrom}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-muted">Rate</span>
                  <span className="font-semibold">{fxQuote.rate || fxQuote.exchangeRate}</span>
                </div>
                <div className="border-t border-border/60" />
                <div className="flex justify-between font-bold">
                  <span className="text-text-primary">You receive</span>
                  <span className="text-green text-lg">{fxQuote.toAmount || fxQuote.convertedAmount} {fxTo}</span>
                </div>
              </div>
            )}

            <Button className="w-full" loading={fxConverting} disabled={!fxQuote} onClick={handleFxConvert}>
              Convert {fxFrom} → {fxTo}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
